import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { redirectToLogin } from '@/lib/identityApi';
import { Loader2, LogOut } from 'lucide-react';

/**
 * Login page — redirects to TW1 Identity Platform.
 *
 * Normaal: auto-redirect naar id.truckwash1.cloud na 200ms.
 *
 * Uitzondering: als `?logout=1` of `sessionStorage.tw1_just_logged_out`
 * (< 60s) aanwezig is, toon een expliciete "Log in" knop i.p.v.
 * auto-redirect. Dit breekt de auto-reauth-loop die ontstaat doordat
 * Identity Platform's `tw1_id_session` cookie niet vanuit de browser
 * gecleared kan worden (CORS + SameSite=Lax — zie lib/identityApi.js).
 * De user moet expliciet opnieuw klikken om in te loggen.
 */
const LOGOUT_GRACE_MS = 60_000;

const Login = () => {
  const [params] = useSearchParams();
  // eslint-disable-next-line no-unused-vars
  const [justLoggedOut, _setJustLoggedOut] = useState(() => {
    // Sync init: als `?logout=1` aanwezig → direct UI renderen zonder
    // flash-redirect. Dit voorkomt de race met useEffect's setTimeout.
    if (params.get('logout') === '1') return true;
    try {
      const ts = Number(sessionStorage.getItem('tw1_just_logged_out') || 0);
      return ts > 0 && Date.now() - ts < LOGOUT_GRACE_MS;
    } catch { return false; }
  });

  useEffect(() => {
    if (justLoggedOut) {
      // Consumeer sessionStorage flag — alleen eerste bezoek telt.
      try { sessionStorage.removeItem('tw1_just_logged_out'); } catch {}
      return; // GEEN auto-redirect
    }
    const t = setTimeout(redirectToLogin, 200);
    return () => clearTimeout(t);
  }, [justLoggedOut]);

  if (justLoggedOut) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md text-center space-y-4" data-testid="login-logged-out">
          <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto">
            <LogOut className="w-6 h-6 text-green-600" />
          </div>
          <h1 className="text-xl font-semibold text-[#1f2548]">Je bent uitgelogd</h1>
          <p className="text-sm text-slate-600">
            Klik op de knop hieronder om opnieuw in te loggen bij TW1 Identity Platform.
          </p>
          <button
            onClick={redirectToLogin}
            className="px-6 py-3 bg-[#1f2548] hover:bg-[#2a3060] text-white rounded-lg text-sm font-medium transition-colors"
            data-testid="login-after-logout"
          >
            Opnieuw inloggen
          </button>
          <p className="text-xs text-slate-400 pt-2">
            Om met een ander account in te loggen: wis je browser-cookies voor id.truckwash1.cloud
            of gebruik een incognito-venster.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center space-y-3" data-testid="login-redirect">
        <Loader2 className="w-8 h-8 animate-spin text-[#1f2548] mx-auto" />
        <h1 className="text-lg font-semibold text-[#1f2548]">Doorsturen naar Identity Platform...</h1>
        <p className="text-sm text-slate-500">Je wordt doorverwezen naar id.truckwash1.cloud om in te loggen.</p>
        <button
          onClick={redirectToLogin}
          className="text-xs text-slate-400 hover:text-[#1f2548] underline"
          data-testid="login-redirect-manual"
        >
          Klik hier als je niet automatisch wordt doorgestuurd
        </button>
      </div>
    </div>
  );
};

export default Login;
